import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrderService } from '../../services/orders/order.service';
import { OrderDetailService } from '../../services/orders/order-detail.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { interval, Subscription } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { OrderPreviewDialogComponent } from '../../dialogs/order-preview-dialog/order-preview-dialog.component';
// OrderPreviewDialogComponent removed – no dialog usage

interface GroupCard {
  itemUuids: string[];
  name: string;
  qty: number;
  options?: Array<{ name: string; qty: number }>;
  status: string;
  since: Date;
  isAdvancing?: boolean;
}

interface OrderCard {
  orderUuid: string;
  orderId?: string;
  tableUuid?: string;
  tableName?: string;
  customerName?: string;
  notes?: string;
  since: Date;            // earliest among groups
  status: string;         // aggregated
  groups: GroupCard[];
}

@Component({
  selector: 'app-ready-orders',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './ready-orders.component.html',
  styleUrls: ['./ready-orders.component.scss']
})
export class ReadyOrdersComponent implements OnInit, OnDestroy {
  loading = false;
  orders: OrderCard[] = [];
  loadError = false;
  private sub?: Subscription;
  private readonly POLL_MS = 30000; // 30 seconds

  constructor(private orderService: OrderService, private orderDetail: OrderDetailService, private router: Router, private toast: NgxToastrService, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.load();
    this.sub = interval(this.POLL_MS).subscribe(() => this.load());
  }

  statusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'pending') return 's-pending';
    if (s === 'sent') return 's-sent';
    if (s === 'ready') return 's-ready';
    if (s === 'delivered') return 's-delivered';
    return '';
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  refresh() {
    this.load(true);
  }

  private load(force = false) {
    if (this.loading && !force) return;

    this.loadError = false;
    this.loading = true;
    const ready$ = this.orderService.getReadyOrderItems();
    const pending$ = this.orderService.getPendingOrderItems();
    ready$.subscribe({
      next: (ready: any[]) => {
        pending$.subscribe({
          next: (pending: any[]) => this.processItems(ready || [], pending || []),
          error: () => this.error(),
          complete: () => this.loading = false
        })
      },
      error: () => this.error()
    });
  }

  private processItems(readyItems: any[], pendingItems: any[]) {
    // Group by ORDER, then by menuProductUuid within each order
    const orders = new Map<string, OrderCard>();

    const add = (item: any, status: string) => {
      const orderUuid = item?.order?.orderHashId || item?.order?.hashId || item?.orderHashId || item?.orderUuid || item?.order?.uuid;
      const itemUuid = item?.uuid || item?.id || item?.orderDetailUuid || item?.orderDetailId;
      const mpUuid = item?.menuProductUuid || item?.menuProduct?.uuid;
      if (!orderUuid || !itemUuid || !mpUuid) return;

      const displayId = item?.order?.id || item?.order?.orderId || item?.order?.orderNumber || item?.orderId || item?.orderNumber || undefined;
      const tableUuid = item?.tableUuid || item?.order?.tableUuid || item?.order?.table?.uuid || item?.table?.uuid;
      const tableName = item?.tableName || item?.order?.table?.name || item?.table?.name || undefined;
      const customerName = item?.order?.clientName;
      const mpName = item?.menuProduct?.name || item?.product?.name || 'Producto';
      const since = new Date(item?.readyAt || item?.processedAt || item?.createdAt || new Date());
      const selectedCount = Array.isArray(item?.selectedOptions) ? item.selectedOptions.length : Number(item?.selectedOptionsCount || 0);
      const qty = Number(item?.qty || 0);

      if (!orders.has(orderUuid)) {
        orders.set(orderUuid, {
          orderUuid: String(orderUuid),
          orderId: displayId,
          tableUuid,
          tableName: tableName || (tableUuid ? 'Mesa' : 'Sin mesa'),
          customerName,
          since,
          status: String(status).toLowerCase(),
          groups: []
        });
      } else {
        const ord = orders.get(orderUuid)!;
        ord.since = new Date(Math.min(ord.since.getTime(), since.getTime()));
        const rank = (s: string) => ['pending', 'sent', 'ready', 'delivered'].indexOf((s || '').toLowerCase());
        ord.status = rank(status) < rank(ord.status) ? String(status).toLowerCase() : ord.status;
      }

      const ord = orders.get(orderUuid)!;
      let grp = ord.groups.find(g => g.name === mpName);
      if (!grp) {
        grp = { itemUuids: [], name: mpName, qty: 0, options: [], status: String(status).toLowerCase(), since };
        ord.groups.push(grp);
      }
      grp.itemUuids.push(String(itemUuid));
      grp.qty += (selectedCount > 0 ? selectedCount : qty);
      grp.since = new Date(Math.min(grp.since.getTime(), since.getTime()));
      const rank = (s: string) => ['pending', 'sent', 'ready', 'delivered'].indexOf((s || '').toLowerCase());
      grp.status = rank(status) < rank(grp.status) ? String(status).toLowerCase() : grp.status;

      const opts: any[] = Array.isArray(item?.selectedOptions) ? item.selectedOptions : [];
      opts.forEach(o => {
        const nm = String(o?.name || o?.nameSnapshot || '').trim();
        if (!nm) return;
        const found = grp.options!.find(x => x.name === nm);
        if (found) found.qty += 1; else grp.options!.push({ name: nm, qty: 1 });
      });
    };

    (pendingItems || []).forEach(it => add(it, 'pending'));
    (readyItems || []).forEach(it => add(it, 'ready'));

    this.orders = Array.from(orders.values()).sort((a, b) => a.since.getTime() - b.since.getTime());
  }

  private error() {
    this.loading = false;
    this.loadError = true;
    // Show a toast for service errors instead of implying empty results
    this.toast.error('No se pudieron cargar las órdenes listas. Intenta nuevamente.', 'toast-top-right');
    console.error('Error loading ready orders');
  }

  timeFrom(dt: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(dt).getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;

    const diffHrs = Math.floor(diffMins / 60);
    return `Hace ${diffHrs} h`;
  }

  openOrder(ord: OrderCard) {
    this.dialog.open(OrderPreviewDialogComponent, {
      width: '520px',
      data: {
        orderId: ord.orderId,
        tableLabel: ord.tableName,
        customerName: ord.customerName,
        since: ord.since,
        status: ord.status,
        groups: (ord.groups || []).map(g => ({ name: g.name, qty: g.qty, options: g.options }))
      }
    });
  }

  nextStatus(target: { status: string }): string | null {
    const s = (target.status || '').toLowerCase();
    if (s === 'pending') return 'sent';
    if (s === 'sent') return 'ready';
    if (s === 'ready') return 'delivered';
    return null;
  }

  onAdvanceGroup(ord: OrderCard, grp: GroupCard) {
    const next = this.nextStatus(grp);
    if (!next) return;
    grp.isAdvancing = true;
    const uuids = Array.isArray(grp.itemUuids) ? grp.itemUuids : [];
    this.orderDetail.processItems(ord.orderUuid, uuids, next, true).subscribe({
      next: () => {
        grp.status = next;
        grp.isAdvancing = false;
        if (next === 'delivered') {
          ord.groups = ord.groups.filter(g => g !== grp);
          this.orders = this.orders.filter(o => o.groups.length > 0);
        }
      },
      error: () => {
        grp.isAdvancing = false;
        this.toast.error('No se pudo avanzar el estado del grupo.', 'toast-top-right');
      }
    });
  }

  isNewOrder(ord: OrderCard): boolean {
    const fiveMinutesAgo = new Date();
    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
    return ord.since > fiveMinutesAgo;
  }
}
